/**
 * Landing-page renderer: block tree → complete HTML document.
 *
 * Runs unchanged in the editor preview and in the edge Worker, so what the
 * operator sees is literally what the visitor gets.
 *
 * Two rules shape the output:
 *  · Everything is inlined. No external CSS, font or script — the page must
 *    render on first byte and must not leak the visitor's IP to a third party
 *    before they consented.
 *  · Marketing pixels are NOT emitted until consent is given. A cookie banner
 *    that appears after the tag already fired is theatre.
 */

import { TEMA_PADRAO, type Bloco, type ImagemBloco, type PaginaLanding } from './blocos.js'
import { normalizarFormulario, type CampoFormulario } from './formulario.js'
import {
  CAPTCHA_DESLIGADO,
  captchaPublico,
  mesclarPixels,
  pixelsDaConfig,
  type ConfigCaptcha,
  type ConfigMedicao,
  type Pixel,
} from './medicao.js'

/** Escapes text for HTML body context. */
export function esc(valor: unknown): string {
  return String(valor ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** Escapes a value that will sit inside a JS string literal. */
function escJs(valor: unknown): string {
  return JSON.stringify(String(valor ?? ''))
}

interface Item {
  titulo?: string
  texto?: string
  valor?: string
  rotulo?: string
  pergunta?: string
  resposta?: string
}

function itens(props: Record<string, unknown>): Item[] {
  return Array.isArray(props.itens) ? (props.itens as Item[]) : []
}

/**
 * Section classes from a block's layout.
 *
 * `--cols` is a custom property rather than a class per count because the CSS
 * that consumes it caps the value on narrow screens: `repeat(auto-fit,
 * minmax(240px, 1fr))` bounded by `--cols` gives three columns on a desktop and
 * one on a phone with a single rule, instead of three media queries that
 * someone will eventually update out of sync.
 */
function classesLayout(b: Bloco): { classe: string; estilo: string } {
  const l = b.layout ?? {}
  const partes = ['secao']
  if (l.fundo === 'suave') partes.push('fundo')
  if (l.fundo === 'escuro') partes.push('fundo-escuro')
  if (l.fundo === 'marca') partes.push('fundo-marca')
  if (l.alinhamento === 'centro') partes.push('centro')
  if (l.espacamento === 'compacto') partes.push('compacta')
  if (l.espacamento === 'amplo') partes.push('ampla')
  return {
    classe: partes.join(' '),
    estilo: l.colunas ? ` style="--cols:${l.colunas}"` : '',
  }
}

/**
 * Renders a responsive `<picture>`.
 *
 * Width and height are always emitted so the browser reserves the space before
 * the bytes arrive — a page that reflows when the hero photo loads is a page
 * where someone taps the wrong button. `sizes` tells the phone to fetch the
 * 800px variant instead of the 1600px one, which is the difference between
 * 60 kB and 240 kB on mobile data.
 */
function figura(
  img: ImagemBloco,
  classe: string,
  prioridade = false,
  baseMidia = '',
): string {
  // Absoluta quando a página é servida de fora da nossa origem — sob um
  // subcaminho de outro domínio (`.../publicacoes/`), `/publico/midia/x`
  // resolve na raiz DAQUELE domínio e não existe lá. Relativa por padrão:
  // é mais curta e é o caso de hoje.
  const base = esc(urlAbsoluta(img.url, baseMidia))
  const carregamento = prioridade
    ? ' fetchpriority="high"'
    : ' loading="lazy" decoding="async"'

  return `<picture>
  <source type="image/avif" srcset="${base}?l=800 800w, ${base}?l=1600 1600w"
    sizes="(max-width: 760px) 100vw, 1080px">
  <img src="${base}?l=1600&f=jpeg" alt="${esc(img.alt)}"
    width="${img.largura}" height="${img.altura}" class="${classe}"${carregamento}>
</picture>`
}

function imagemDe(props: Record<string, unknown>): ImagemBloco | null {
  const img = props.imagem as Partial<ImagemBloco> | undefined
  if (!img?.url || typeof img.url !== 'string') return null
  return {
    url: img.url,
    alt: String(img.alt ?? ''),
    largura: Number(img.largura) || 1600,
    altura: Number(img.altura) || 900,
  }
}



const TIPO_INPUT: Record<string, string> = {
  texto: 'text',
  email: 'email',
  telefone: 'tel',
  cnpj: 'text',
  numero: 'number',
  data: 'date',
}

const AUTOCOMPLETE: Record<string, string> = {
  nome: 'name',
  email: 'email',
  telefone: 'tel',
  razaoSocial: 'organization',
  cargo: 'organization-title',
  municipio: 'address-level2',
  uf: 'address-level1',
}

/** One form field, as HTML. */
function renderCampo(c: CampoFormulario): string {
  const req = c.obrigatorio ? ' required' : ''
  const marca = c.obrigatorio ? ' <span aria-hidden="true">*</span>' : ''
  const largura = c.largura === 'metade' ? ' campo-metade' : ''
  const ajuda = c.ajuda ? `<small class="ajuda-campo">${esc(c.ajuda)}</small>` : ''
  const auto = AUTOCOMPLETE[c.nome] ? ` autocomplete="${AUTOCOMPLETE[c.nome]}"` : ''
  const ph = c.placeholder ? ` placeholder="${esc(c.placeholder)}"` : ''

  if (c.tipo === 'checkbox') {
    return `<label class="campo consentimento${largura}">
  <input type="checkbox" name="${esc(c.nome)}"${req} />
  <span>${esc(c.rotulo)}</span>
</label>`
  }

  if (c.tipo === 'textarea') {
    return `<label class="campo${largura}">${esc(c.rotulo)}${marca}
  <textarea name="${esc(c.nome)}" rows="4"${req}${ph}></textarea>${ajuda}
</label>`
  }

  if (c.tipo === 'selecao') {
    const opcoes = (c.opcoes ?? [])
      .filter((o) => o.trim())
      .map((o) => `<option value="${esc(o)}">${esc(o)}</option>`)
      .join('')
    return `<label class="campo${largura}">${esc(c.rotulo)}${marca}
  <select name="${esc(c.nome)}"${req}><option value="">Selecione…</option>${opcoes}</select>${ajuda}
</label>`
  }

  const extra =
    c.tipo === 'cnpj'
      ? ' inputmode="numeric" maxlength="18" placeholder="00.000.000/0000-00"'
      : c.tipo === 'telefone'
        ? ' inputmode="tel"'
        : ph

  return `<label class="campo${largura}">${esc(c.rotulo)}${marca}
  <input type="${TIPO_INPUT[c.tipo] ?? 'text'}" name="${esc(c.nome)}"${req}${auto}${extra} />${ajuda}
</label>`
}

// ---------------------------------------------------------------------------
// Blocos
// ---------------------------------------------------------------------------

function renderBloco(b: Bloco, baseMidia = ''): string {
  const p = b.props
  const { classe, estilo } = classesLayout(b)

  switch (b.tipo) {
    case 'hero': {
      const foto = imagemDe(p)
      return `
<section class="hero${foto ? ' hero-foto' : ''}">
  ${foto ? `<div class="hero-fundo">${figura(foto, 'cobre', true, baseMidia)}</div>` : ''}
  <div class="wrap">
    ${p.selo ? `<p class="selo">${esc(p.selo)}</p>` : ''}
    <h1>${esc(p.titulo)}</h1>
    ${p.subtitulo ? `<p class="sub">${esc(p.subtitulo)}</p>` : ''}
    ${p.textoBotao ? `<a class="btn btn-acao" href="#formulario">${esc(p.textoBotao)}</a>` : ''}
  </div>
</section>`
    }

    case 'imagem': {
      const foto = imagemDe(p)
      if (!foto) return ''
      return `
<section class="${classe}"${estilo}>
  <div class="wrap">
    <figure class="figura">
      ${figura(foto, 'larga', false, baseMidia)}
      ${p.legenda ? `<figcaption>${esc(p.legenda)}</figcaption>` : ''}
    </figure>
  </div>
</section>`
    }

    case 'texto':
      return `
<section class="${classe}"${estilo}>
  <div class="wrap estreito">
    ${p.titulo ? `<h2>${esc(p.titulo)}</h2>` : ''}
    <p class="corpo">${esc(p.corpo)}</p>
  </div>
</section>`

    case 'colunas':
      return `
<section class="${classe}"${estilo}>
  <div class="wrap">
    ${p.titulo ? `<h2>${esc(p.titulo)}</h2>` : ''}
    <div class="grade">
      ${itens(p)
        .map(
          (i) => `<div class="coluna">
        <h3>${esc(i.titulo)}</h3>
        <p>${esc(i.texto)}</p>
      </div>`,
        )
        .join('')}
    </div>
  </div>
</section>`

    case 'beneficios':
      return `
<section class="${classe}"${estilo}>
  <div class="wrap">
    <h2>${esc(p.titulo)}</h2>
    <div class="grade">
      ${itens(p)
        .map(
          (i, n) => `<div class="cartao">
        <span class="numero">${n + 1}</span>
        <h3>${esc(i.titulo)}</h3>
        <p>${esc(i.texto)}</p>
      </div>`,
        )
        .join('')}
    </div>
  </div>
</section>`

    case 'prazo':
      return `
<section class="${classe}"${estilo}>
  <div class="wrap estreito">
    <div class="aviso">
      <h2>${esc(p.titulo)}</h2>
      <p>${esc(p.texto)}</p>
      ${p.referencia ? `<p class="ref">${esc(p.referencia)}</p>` : ''}
    </div>
  </div>
</section>`

    case 'prova':
      return `
<section class="${classe}"${estilo}>
  <div class="wrap">
    <h2>${esc(p.titulo)}</h2>
    <div class="numeros">
      ${itens(p)
        .map(
          (i) => `<div>
        <strong>${esc(i.valor)}</strong>
        <span>${esc(i.rotulo)}</span>
      </div>`,
        )
        .join('')}
    </div>
  </div>
</section>`

    case 'faq':
      return `
<section class="${classe}"${estilo}>
  <div class="wrap estreito">
    <h2>${esc(p.titulo)}</h2>
    ${itens(p)
      .map(
        (i) => `<details>
      <summary>${esc(i.pergunta)}</summary>
      <p>${esc(i.resposta)}</p>
    </details>`,
      )
      .join('')}
  </div>
</section>`

    case 'formulario': {
      // Uma normalização só, compartilhada com o editor e com a API: página
      // antiga guarda o consentimento em `props.textoConsentimento` e os campos
      // implícitos em `pedirCnpj`/`pedirTelefone`.
      const { campos: cs, acoes: a } = normalizarFormulario(p)
      return `
<section class="${classe}" id="formulario"${estilo}>
  <div class="wrap estreito">
    <h2>${esc(p.titulo)}</h2>
    ${p.subtitulo ? `<p class="sub-form">${esc(p.subtitulo)}</p>` : ''}
    <form id="form-lead" novalidate
      data-destino="${esc(a.destino)}"
      data-url="${esc(a.urlRedirecionamento ?? '')}"
      data-sucesso="${esc(a.mensagemSucesso)}">
      <div class="grade-form">
        ${cs.map(renderCampo).join('\n        ')}
      </div>
      <label class="campo consentimento">
        <input type="checkbox" name="consentimento" required />
        <span>${esc(a.textoConsentimento)}</span>
      </label>
      <div class="captcha" id="captcha-lead"></div>
      <button class="btn btn-acao largo" type="submit">${esc(a.textoBotao)}</button>
      <p class="estado" id="estado-form" role="status"></p>
    </form>
  </div>
</section>`
    }

    case 'cta':
      return `
<section class="${classe} cta"${estilo}>
  <div class="wrap estreito centro">
    <h2>${esc(p.titulo)}</h2>
    <a class="btn btn-acao" href="#formulario">${esc(p.textoBotao)}</a>
  </div>
</section>`

    case 'contato': {
      const canais: string[] = []

      const zap = String(p.whatsapp ?? '').replace(/\D/g, '')
      if (zap) {
        // wa.me exige o número em E.164 sem o "+". Dez ou onze dígitos é número
        // nacional — ganha o 55; começando por 55 com 12+, já veio completo.
        const completo = zap.length <= 11 ? `55${zap}` : zap
        const msg = String(p.mensagemWhatsapp ?? '').trim()
        const href = `https://wa.me/${completo}${msg ? `?text=${encodeURIComponent(msg)}` : ''}`
        canais.push(`<a class="canal" href="${esc(href)}" target="_blank" rel="noopener">
        <span class="canal-icone" aria-hidden="true"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.6-6.1c-.3-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.4-3c-.3-.4 0-.5.1-.7l.4-.5c.1-.2.2-.3.3-.5v-.5L9.7 7.6c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5 0-.7.3-.2.3-.9.9-.9 2.1s.9 2.4 1 2.6c.1.2 1.8 2.8 4.4 3.9.6.3 1.1.4 1.5.5.6.2 1.2.2 1.6.1.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2l-.4-.2Z"/></svg></span>
        <span class="canal-corpo"><strong>WhatsApp</strong><span>${esc(p.whatsapp)}</span></span>
      </a>`)
      }

      const tels = (Array.isArray(p.telefones) ? (p.telefones as unknown[]) : [])
        .map((t) => String(t ?? '').trim())
        .filter(Boolean)
      for (const tel of tels) {
        const dig = tel.replace(/\D/g, '')
        const href = `tel:${dig.length >= 12 ? `+${dig}` : `+55${dig}`}`
        canais.push(`<a class="canal" href="${esc(href)}">
        <span class="canal-icone" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z"/></svg></span>
        <span class="canal-corpo"><strong>Telefone</strong><span>${esc(tel)}</span></span>
      </a>`)
      }

      const email = String(p.email ?? '').trim()
      if (email) {
        const assunto = String(p.assuntoEmail ?? '').trim()
        const href = `mailto:${email}${assunto ? `?subject=${encodeURIComponent(assunto)}` : ''}`
        canais.push(`<a class="canal" href="${esc(href)}">
        <span class="canal-icone" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/></svg></span>
        <span class="canal-corpo"><strong>E-mail</strong><span>${esc(email)}</span></span>
      </a>`)
      }

      if (canais.length === 0) return ''
      return `
<section class="${classe}"${estilo}>
  <div class="wrap">
    ${p.titulo ? `<h2>${esc(p.titulo)}</h2>` : ''}
    ${p.subtitulo ? `<p class="sub-form">${esc(p.subtitulo)}</p>` : ''}
    <div class="canais">
      ${canais.join('\n      ')}
    </div>
  </div>
</section>`
    }

    case 'rodape':
      return `
<footer>
  <div class="wrap">
    <p>${esc(p.texto)}</p>
    ${p.contato ? `<p class="contato">${esc(p.contato)}</p>` : ''}
    <p class="legal">
      <a href="/privacidade">Política de Privacidade</a> ·
      <a href="/termos">Termos de Uso</a> ·
      <a href="/descadastro">Descadastrar</a>
    </p>
  </div>
</footer>`

    default:
      return ''
  }
}

// ---------------------------------------------------------------------------
// CSS
// ---------------------------------------------------------------------------

function css(tema: PaginaLanding['tema']): string {
  const t = { ...TEMA_PADRAO, ...tema }
  return `
*{box-sizing:border-box;margin:0;padding:0}
html{-webkit-text-size-adjust:100%}
body{font-family:${t.fonte};color:${t.corTexto};background:${t.corFundo};line-height:1.6;font-size:17px}
.wrap{max-width:1080px;margin:0 auto;padding:0 24px}
.estreito{max-width:720px}
.centro{text-align:center}
h1{font-size:clamp(30px,5vw,48px);line-height:1.12;letter-spacing:-.02em;font-weight:700}
h2{font-size:clamp(23px,3vw,32px);line-height:1.2;letter-spacing:-.01em;font-weight:650;margin-bottom:18px}
h3{font-size:18px;font-weight:600;margin-bottom:6px}
.secao{padding:64px 0}
.secao.compacta{padding:40px 0}
.secao.ampla{padding:88px 0}
.fundo{background:#f6f8fb}
.fundo-escuro{background:#0f172a;color:#e2e8f0}
.fundo-escuro h2,.fundo-escuro h3{color:#f8fafc}
.fundo-escuro p,.fundo-escuro .corpo{color:#94a3b8}
.fundo-marca{background:${t.corPrimaria};color:#fff}
.fundo-marca h2,.fundo-marca h3{color:#fff}
.fundo-marca p,.fundo-marca .corpo{color:rgba(255,255,255,.86)}
/* --cols é um MÁXIMO. O minmax colapsa para uma coluna no celular sem media
   query nenhuma: três regras que se desencontram é como uma landing page
   acaba com três colunas espremidas num aparelho de 360px. */
.grade{display:grid;gap:22px;margin-top:32px;
  grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr))}
@media(min-width:760px){.grade{grid-template-columns:repeat(var(--cols,3),1fr)}}
.coluna h3{margin-bottom:8px}
.coluna p{color:#64748b;font-size:15px}
.grade-form{display:grid;gap:16px;grid-template-columns:1fr}
@media(min-width:620px){.grade-form{grid-template-columns:1fr 1fr}
  .campo{grid-column:1/-1}.campo-metade{grid-column:auto}}
.ajuda-campo{display:block;margin-top:5px;font-size:12px;color:#64748b;font-weight:400}
select,textarea{width:100%;margin-top:6px;padding:13px 14px;border:1px solid #cfd8e3;border-radius:10px;font:inherit;font-size:16px;background:#fff}
select:focus,textarea:focus{outline:2px solid ${t.corPrimaria};outline-offset:1px;border-color:${t.corPrimaria}}
textarea{resize:vertical;min-height:96px}
.hero{background:linear-gradient(160deg,${t.corPrimaria} 0%,#072644 100%);color:#fff;padding:88px 0 96px}
.hero-foto{position:relative;isolation:isolate}
.hero-fundo{position:absolute;inset:0;z-index:-1;overflow:hidden}
.hero-fundo picture,.hero-fundo img{width:100%;height:100%;display:block}
/* A sobreposição não é enfeite: sem ela o texto branco some sobre a parte
   clara da foto, e o contraste some junto com a legibilidade. */
.hero-fundo::after{content:'';position:absolute;inset:0;
  background:linear-gradient(100deg,${t.corPrimaria}f2 0%,${t.corPrimaria}cc 45%,#07264499 100%)}
.cobre{object-fit:cover}
picture{display:block}
img{max-width:100%;height:auto}
.figura{margin:0}
.larga{width:100%;border-radius:14px;display:block}
figcaption{margin-top:10px;font-size:14px;color:#64748b;text-align:center}
.hero .selo{font-size:12px;letter-spacing:.14em;text-transform:uppercase;opacity:.72;margin-bottom:20px}
.hero .sub{margin-top:20px;font-size:19px;opacity:.9;max-width:640px}
.btn{display:inline-block;margin-top:32px;padding:15px 30px;border-radius:10px;font-weight:600;text-decoration:none;border:0;cursor:pointer;font-size:16px;transition:filter .15s}
.btn:hover{filter:brightness(1.08)}
.btn-acao{background:${t.corAcao};color:#fff}
.largo{width:100%;margin-top:24px}
.corpo{font-size:17px;color:#475569}
.cartao{background:#fff;border:1px solid #e4e9f0;border-radius:14px;padding:26px}
.numero{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:99px;background:${t.corPrimaria};color:#fff;font-size:14px;font-weight:600;margin-bottom:14px}
.cartao p{color:#64748b;font-size:15px}
.aviso{border-left:4px solid ${t.corAcao};background:#fffbeb;padding:26px 28px;border-radius:0 12px 12px 0}
.aviso p{color:#78350f}
.ref{margin-top:12px;font-size:13px;opacity:.75}
.numeros{display:grid;gap:28px;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-top:32px;text-align:center}
.numeros strong{display:block;font-size:40px;font-weight:700;color:${t.corPrimaria};font-variant-numeric:tabular-nums}
.numeros span{font-size:14px;color:#64748b}
details{border-bottom:1px solid #e4e9f0;padding:18px 0}
summary{cursor:pointer;font-weight:600;list-style:none}
summary::-webkit-details-marker{display:none}
summary::after{content:'+';float:right;color:${t.corPrimaria};font-weight:400;font-size:22px;line-height:1}
details[open] summary::after{content:'\\2212'}
details p{margin-top:12px;color:#475569;font-size:16px}
form{margin-top:26px;display:flex;flex-direction:column;gap:16px}
.secao.centro .grade,.secao.centro .grade-form{text-align:left}
label{font-size:14px;font-weight:500;display:block}
input:not([type=checkbox]){width:100%;margin-top:6px;padding:13px 14px;border:1px solid #cfd8e3;border-radius:10px;font:inherit;font-size:16px;background:#fff}
input:focus{outline:2px solid ${t.corPrimaria};outline-offset:1px;border-color:${t.corPrimaria}}
.consentimento{display:flex;gap:11px;align-items:flex-start;font-weight:400;font-size:13px;color:#475569;line-height:1.5}
.consentimento input{margin-top:3px;width:17px;height:17px;flex-shrink:0}
.sub-form{color:#64748b;font-size:16px}
.estado{font-size:14px;min-height:20px}
.estado.ok{color:#0f7048}.estado.erro{color:#b42318}
.enviado{margin-top:26px;padding:24px 26px;border-radius:12px;background:#ecfdf5;border:1px solid #a7f3d0;color:#065f46;font-size:16px}
.cta{background:${t.corPrimaria};color:#fff}
.canais{display:grid;gap:14px;margin-top:26px;grid-template-columns:repeat(auto-fit,minmax(min(250px,100%),1fr))}
.canal{display:flex;gap:14px;align-items:center;background:#fff;border:1px solid #e4e9f0;border-radius:14px;padding:18px 20px;text-decoration:none;color:inherit;transition:border-color .15s,box-shadow .15s}
.canal:hover{border-color:${t.corPrimaria};box-shadow:0 2px 10px rgba(15,23,42,.06)}
.canal-icone{display:flex;align-items:center;justify-content:center;width:44px;height:44px;flex-shrink:0;border-radius:12px;background:${t.corPrimaria}14;color:${t.corPrimaria}}
.canal-icone svg{width:22px;height:22px}
.canal-corpo{display:flex;flex-direction:column;min-width:0}
.canal-corpo strong{font-size:15px;font-weight:600}
.canal-corpo span{font-size:14px;color:#64748b;overflow-wrap:anywhere}
.fundo-escuro .canal,.fundo-marca .canal{background:rgba(255,255,255,.06);border-color:rgba(255,255,255,.18);color:#fff}
.fundo-escuro .canal-corpo span,.fundo-marca .canal-corpo span{color:rgba(255,255,255,.75)}
footer{background:#0f172a;color:#94a3b8;padding:44px 0;font-size:14px}
footer .contato{margin-top:8px}
footer .legal{margin-top:18px;font-size:13px}
footer a{color:#cbd5e1;text-decoration:underline}
.cookies{position:fixed;left:0;right:0;bottom:0;background:#0f172a;color:#e2e8f0;padding:18px 24px;display:flex;gap:16px;align-items:center;flex-wrap:wrap;justify-content:center;font-size:14px;z-index:99}
.cookies button{padding:9px 18px;border-radius:8px;border:0;cursor:pointer;font:inherit;font-weight:600}
.captcha{margin:14px 0}
.captcha:empty{display:none}
.cookies .aceitar{background:${t.corAcao};color:#fff}
.cookies .recusar{background:transparent;color:#cbd5e1;border:1px solid #475569}
@media(prefers-reduced-motion:reduce){*{transition:none!important;animation:none!important}}`
}

// ---------------------------------------------------------------------------
// Script: UTM, consentimento e captura
// ---------------------------------------------------------------------------

function script(
  slug: string,
  urlApi: string,
  pixels: Pixel[],
  captcha: ConfigCaptcha = CAPTCHA_DESLIGADO,
): string {
  return `
(function(){
  var CHAVE='egba_cookies';
  var CAPTCHA=${JSON.stringify(captchaPublico(captcha))};
  var params=new URLSearchParams(location.search);
  var utm={};
  ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','gclid','fbclid'].forEach(function(k){
    var v=params.get(k); if(v) utm[k]=v;
  });
  // A atribuição precisa sobreviver à navegação interna: sem isto, quem vê a
  // página, clica em algo e volta chega ao formulário sem origem nenhuma.
  try{
    var salvo=sessionStorage.getItem('egba_utm');
    if(Object.keys(utm).length) sessionStorage.setItem('egba_utm',JSON.stringify(utm));
    else if(salvo) utm=JSON.parse(salvo);
  }catch(e){}

  // ---- Rastreio do envio de campanha (primeira parte, sem cookie) ----
  // "eid" é o id do envio que trouxe a pessoa, carimbado no link pela régua.
  // O beacon marca o clique; o mesmo id volta no formulário e fecha o funil
  // enviado -> clicou -> preencheu. Não é o pixel: pixel é de terceiro e
  // espera o consentimento; isto mede a nossa própria mensagem.
  var eid=params.get('eid')||null;
  try{
    if(eid) sessionStorage.setItem('egba_eid',eid);
    else eid=sessionStorage.getItem('egba_eid');
  }catch(e){}
  if(params.get('eid')){
    try{
      fetch(${escJs(urlApi)}+'/publico/rastreio/clique?eid='+encodeURIComponent(eid)+'&lp='+encodeURIComponent(${escJs(slug)}),{keepalive:true}).catch(function(){});
    }catch(e){}
  }

  // ---- Consentimento: pixel nenhum dispara antes ----
  var PIXELS=${JSON.stringify(pixels)};
  var ADS=[];               // AW-… com rótulo de conversão, usados no lead
  var carregados=false;
  var comecou=false;        // alguém já tocou no formulário

  function tagScript(src){
    var s=document.createElement('script'); s.async=true; s.src=src;
    document.head.appendChild(s); return s;
  }
  function gtagPronto(){
    if(window.gtag) return;
    window.dataLayer=window.dataLayer||[];
    window.gtag=function(){window.dataLayer.push(arguments)};
    window.gtag('js',new Date());
  }

  function carregarPixels(){
    // Duas chamadas acontecem: quem já tinha aceitado carrega no load, e a
    // barra de cookies pode chamar de novo. Inicializar o mesmo pixel duas
    // vezes duplica cada evento — e o relatório passa a mentir por dois.
    if(carregados) return;
    carregados=true;

    var gtagIds=[];
    PIXELS.forEach(function(p){
      if(p.provedor==='ga4'||p.provedor==='google_ads'){
        gtagIds.push(p.id);
        if(p.provedor==='google_ads'&&p.rotuloConversao) ADS.push(p);
      }
      if(p.provedor==='gtm'){
        // O contêiner é a porta para qualquer outra tag, sem deploy. Carrega
        // depois do consentimento como todo o resto.
        window.dataLayer=window.dataLayer||[];
        window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});
        tagScript('https://www.googletagmanager.com/gtm.js?id='+encodeURIComponent(p.id));
      }
      if(p.provedor==='meta'){
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
        (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
        window.fbq('init',p.id); window.fbq('track','PageView');
      }
      if(p.provedor==='tiktok'){
        !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
        ttq.methods=['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie'];
        ttq.setAndDefer=function(o,m){o[m]=function(){o.push([m].concat(Array.prototype.slice.call(arguments,0)))}};
        for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
        ttq.instance=function(id){var o=ttq._i[id]||[];for(var j=0;j<ttq.methods.length;j++)ttq.setAndDefer(o,ttq.methods[j]);return o};
        ttq.load=function(id){var u='https://analytics.tiktok.com/i18n/pixel/events.js';
        ttq._i=ttq._i||{};ttq._i[id]=[];ttq._i[id]._u=u;ttq._t=ttq._t||{};ttq._t[id]=+new Date;
        ttq._o=ttq._o||{};ttq._o[id]={};var s=d.createElement('script');s.async=!0;
        s.src=u+'?sdkid='+id+'&lib='+t;var f=d.getElementsByTagName('script')[0];
        f.parentNode.insertBefore(s,f)};}(window,document,'ttq');
        window.ttq.load(p.id); window.ttq.page();
      }
      if(p.provedor==='linkedin'){
        window._linkedin_partner_id=p.id;
        window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];
        window._linkedin_data_partner_ids.push(p.id);
        tagScript('https://snap.licdn.com/li.lms-analytics/insight.min.js');
      }
    });

    if(gtagIds.length){
      gtagPronto();
      tagScript('https://www.googletagmanager.com/gtag/js?id='+encodeURIComponent(gtagIds[0]));
      gtagIds.forEach(function(id){ window.gtag('config',id); });
    }
    // Quem já estava na página antes de aceitar não gerou o "começou a
    // preencher": vale reemitir para não perder a etapa do funil.
    if(comecou) evento('form_start');
  }

  /** Um evento em todos os provedores que estiverem ligados. */
  function evento(nome){
    var META={form_start:'InitiateCheckout',generate_lead:'Lead',view_form:'ViewContent'};
    var TIKTOK={form_start:'InitiateCheckout',generate_lead:'SubmitForm',view_form:'ViewContent'};
    try{ if(window.gtag) window.gtag('event',nome,{page_slug:${escJs(slug)}}); }catch(e){}
    try{ if(window.fbq&&META[nome]) window.fbq('track',META[nome]); }catch(e){}
    try{ if(window.ttq&&TIKTOK[nome]) window.ttq.track(TIKTOK[nome]); }catch(e){}
    try{ if(window.dataLayer) window.dataLayer.push({event:'egba_'+nome,pagina:${escJs(slug)}}); }catch(e){}
    if(nome==='generate_lead'){
      try{ if(window.lintrk) window.lintrk('track',{conversion_id:undefined}); }catch(e){}
      ADS.forEach(function(p){
        try{ window.gtag('event','conversion',{send_to:p.id+'/'+p.rotuloConversao}); }catch(e){}
      });
    }
  }
  function decidir(aceitou){
    try{ localStorage.setItem(CHAVE,JSON.stringify({marketing:aceitou,em:new Date().toISOString(),versao:1})); }catch(e){}
    var b=document.getElementById('cookies'); if(b) b.remove();
    if(aceitou) carregarPixels();
  }
  var previa=null;
  try{ previa=JSON.parse(localStorage.getItem(CHAVE)||'null'); }catch(e){}
  if(previa){ if(previa.marketing) carregarPixels(); }
  else if(PIXELS.length){
    var barra=document.createElement('div');
    barra.className='cookies'; barra.id='cookies';
    barra.innerHTML='<span>Usamos cookies de medição para entender o alcance desta página. Você pode recusar sem perder nenhuma funcionalidade.</span>'+
      '<button class="aceitar" id="ck-sim">Aceitar</button><button class="recusar" id="ck-nao">Recusar</button>';
    document.body.appendChild(barra);
    document.getElementById('ck-sim').onclick=function(){decidir(true)};
    document.getElementById('ck-nao').onclick=function(){decidir(false)};
  }

  // ---- Captura ----
  var form=document.getElementById('form-lead');
  if(!form) return;
  var estado=document.getElementById('estado-form');

  // ---- Funil: viu o formulário → começou a preencher → enviou ----
  try{
    if(window.IntersectionObserver){
      var obs=new IntersectionObserver(function(es){
        if(es[0]&&es[0].isIntersecting){ evento('view_form'); obs.disconnect(); }
      },{threshold:0.4});
      obs.observe(form);
    }
  }catch(e){}
  form.addEventListener('input',function(){
    if(comecou) return;
    comecou=true;
    evento('form_start');
  },{once:false});

  // ---- Desafio anti-robô ----
  // Carrega independentemente do consentimento de marketing: é proteção do
  // formulário, não medição. Ver o cabeçalho de medicao.ts.
  var caixa=document.getElementById('captcha-lead');
  var tokenWidget=null;
  if(CAPTCHA.provedor==='turnstile'&&caixa){
    window.egbaCaptchaPronto=function(){
      try{
        window.turnstile.render(caixa,{sitekey:CAPTCHA.siteKey,language:'pt-br',
          callback:function(t){tokenWidget=t},
          'expired-callback':function(){tokenWidget=null},
          'error-callback':function(){tokenWidget=null}});
      }catch(e){}
    };
    tagScript('https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=egbaCaptchaPronto');
  }
  if(CAPTCHA.provedor==='recaptcha_v3'){
    tagScript('https://www.google.com/recaptcha/api.js?render='+encodeURIComponent(CAPTCHA.siteKey));
  }
  /** Devolve o token, ou null quando não há desafio configurado. */
  function obterToken(){
    if(CAPTCHA.provedor==='turnstile'){
      // O widget resolve sozinho; se ainda não resolveu, não adianta insistir.
      return Promise.resolve(tokenWidget);
    }
    if(CAPTCHA.provedor==='recaptcha_v3'){
      return new Promise(function(ok){
        try{
          window.grecaptcha.ready(function(){
            window.grecaptcha.execute(CAPTCHA.siteKey,{action:'lead'}).then(ok,function(){ok(null)});
          });
        }catch(e){ ok(null); }
      });
    }
    return Promise.resolve(null);
  }

  form.addEventListener('submit',function(ev){
    ev.preventDefault();
    if(!form.reportValidity()) return;
    var botao=form.querySelector('button[type=submit]');
    botao.disabled=true; estado.className='estado'; estado.textContent='Enviando…';
    var d=new FormData(form);

    // Campos canônicos viram colunas do cadastro; o resto vai como resposta
    // livre. O operador pode criar o campo que quiser sem que a API precise
    // saber dele de antemão.
    var CANONICOS={nome:1,email:1,telefone:1,cnpj:1,razaoSocial:1,cargo:1,municipio:1,uf:1};
    var corpo={pagina:${escJs(slug)},utm:utm,referrer:document.referrer||null,
      paginaEntrada:location.href,respostas:{}};
    if(eid) corpo.eid=eid;
    d.forEach(function(v,k){
      if(k==='consentimento') return;
      if(CANONICOS[k]) corpo[k]=v===''?null:v; else corpo.respostas[k]=v;
    });
    corpo.consentimento=d.get('consentimento')==='on';

    obterToken().then(function(token){
      if(token) corpo.captchaToken=token;
      return fetch(${escJs(urlApi)}+'/api/v1/publico/lead',{
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify(corpo)
      });
    }).then(function(r){return r.json().then(function(j){return{ok:r.ok,j:j}})})
    .then(function(res){
      if(!res.ok) throw new Error((res.j.erro&&res.j.erro.mensagem)||'Falha no envio.');
      evento('generate_lead');

      if(form.dataset.destino==='redirecionar' && form.dataset.url){
        location.href=form.dataset.url; return;
      }
      // Some o formulário e deixa só a confirmação: manter os campos na tela
      // depois do envio é o que faz a pessoa mandar de novo.
      var ok=document.createElement('div');
      ok.className='enviado'; ok.setAttribute('role','status');
      ok.textContent=form.dataset.sucesso||'Recebemos sua solicitação.';
      form.replaceWith(ok);
      ok.scrollIntoView({block:'center',behavior:'smooth'});
    })
    .catch(function(e){
      estado.className='estado erro';
      estado.textContent=e.message||'Não foi possível enviar. Tente novamente.';
      botao.disabled=false;
      // O token do Turnstile é de uso único: sem o reset, a segunda tentativa
      // manda o mesmo token, o servidor recusa, e a pessoa fica presa num erro
      // que ela não tem como resolver.
      if(CAPTCHA.provedor==='turnstile'){ tokenWidget=null; try{ window.turnstile.reset(); }catch(e2){} }
    });
  });
})();`
}

// ---------------------------------------------------------------------------
// Documento
// ---------------------------------------------------------------------------

export interface OpcoesRender {
  /** Public API base the form posts to. */
  urlApi: string
  /**
   * Canonical public address of this page.
   *
   * Only needed when something other than the API serves it (the edge Worker).
   * Otherwise it is derived from `urlApi`.
   */
  urlPublica?: string
  /** True in the editor preview: no pixels, no capture. */
  previa?: boolean
  /**
   * Medição da conta — aplicada a TODAS as páginas.
   *
   * Vem de `marketing.medicao`. A página só acrescenta o que for dela; ver o
   * cabeçalho de `medicao.ts` para o porquê de o padrão ser global.
   */
  medicao?: ConfigMedicao | null
  /** Desafio anti-robô do formulário. A chave secreta nunca chega aqui. */
  captcha?: ConfigCaptcha | null
}

/**
 * The image a share card should use.
 *
 * Order: an explicitly configured one, then the hero's photograph, then the
 * first image block. Falling back through the page's own content is what makes
 * a shared link look right without anyone remembering to fill a "social image"
 * field — and that field is never filled.
 */
export function imagemSocial(pagina: PaginaLanding): ImagemBloco | null {
  if (pagina.seo.imagem) {
    return { url: pagina.seo.imagem, alt: pagina.titulo, largura: 1200, altura: 630 }
  }

  const visiveis = pagina.blocos.filter((b) => !b.oculto)
  const hero = visiveis.find((b) => b.tipo === 'hero')
  const doHero = hero ? imagemDe(hero.props) : null
  if (doHero) return doHero

  const bloco = visiveis.find((b) => b.tipo === 'imagem')
  return bloco ? imagemDe(bloco.props) : null
}

/**
 * Turns a possibly-relative media path into the absolute URL a crawler needs.
 *
 * WhatsApp, Telegram, Slack and Facebook all refuse a relative `og:image` — the
 * link preview simply comes out blank, which is the failure nobody notices
 * until the campaign is already running.
 */
function urlAbsoluta(caminho: string, base: string): string {
  if (/^https?:\/\//i.test(caminho)) return caminho
  return `${base.replace(/\/+$/, '')}${caminho.startsWith('/') ? '' : '/'}${caminho}`
}

export function renderizarPagina(pagina: PaginaLanding, opcoes: OpcoesRender): string {
  const visiveis = pagina.blocos.filter((b) => !b.oculto)

  // Enquanto a página mora na nossa origem, `/publico/midia/x` é o suficiente
  // e é mais curto. Quando `urlPublica` diz que ela é servida de outro lugar —
  // um subcaminho de egba.ba.gov.br, por exemplo — esse mesmo caminho passa a
  // apontar para a raiz DAQUELE domínio, onde não existe: toda foto some e
  // sobra o texto alternativo. Aí as URLs precisam ser absolutas.
  const baseMidia = opcoes.urlPublica ? opcoes.urlApi : ''
  const corpo = visiveis.map((b) => renderBloco(b, baseMidia)).join('\n')

  const tituloSeo = pagina.seo.titulo || pagina.titulo
  const descricao = pagina.seo.descricao ?? ''

  const urlPagina =
    opcoes.urlPublica ?? `${opcoes.urlApi.replace(/\/+$/, '')}/publico/lp/${pagina.slug}`

  const social = imagemSocial(pagina)
  // Os parâmetros de tamanho são NOSSOS: só valem para /publico/midia. Grudá-los
  // numa URL externa configurada à mão produziria um endereço que não existe.
  // JPEG na força porque vários rastreadores de link ainda não decodificam AVIF,
  // e um card sem imagem é pior que um card com imagem maior.
  const nossa = social?.url.includes('/publico/midia/') ?? false
  const urlSocial = social
    ? urlAbsoluta(
        nossa && !social.url.includes('?') ? `${social.url}?l=1600&f=jpeg` : social.url,
        opcoes.urlApi,
      )
    : null

  // Na prévia do editor nada é rastreado e nada é capturado: o operador está
  // conferindo o layout, não gerando lead nem consumindo verba de anúncio.
  const pixels = mesclarPixels(pagina.pixels ?? [], pixelsDaConfig(opcoes.medicao))
  const js = opcoes.previa
    ? ''
    : script(pagina.slug, opcoes.urlApi, pixels, captchaPublico(opcoes.captcha))

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(tituloSeo)}</title>
<meta name="description" content="${esc(descricao)}">
<link rel="canonical" href="${esc(urlPagina)}">

<meta property="og:type" content="website">
<meta property="og:site_name" content="Diário Oficial do Estado da Bahia · EGBA">
<meta property="og:locale" content="pt_BR">
<meta property="og:url" content="${esc(urlPagina)}">
<meta property="og:title" content="${esc(tituloSeo)}">
<meta property="og:description" content="${esc(descricao)}">
${
  urlSocial
    ? `<meta property="og:image" content="${esc(urlSocial)}">
<meta property="og:image:secure_url" content="${esc(urlSocial)}">
<meta property="og:image:type" content="image/jpeg">
<meta property="og:image:width" content="${social!.largura}">
<meta property="og:image:height" content="${social!.altura}">
<meta property="og:image:alt" content="${esc(social!.alt)}">`
    : ''
}

<meta name="twitter:card" content="${urlSocial ? 'summary_large_image' : 'summary'}">
<meta name="twitter:title" content="${esc(tituloSeo)}">
<meta name="twitter:description" content="${esc(descricao)}">
${urlSocial ? `<meta name="twitter:image" content="${esc(urlSocial)}">
<meta name="twitter:image:alt" content="${esc(social!.alt)}">` : ''}

<meta name="theme-color" content="${esc(pagina.tema.corPrimaria ?? TEMA_PADRAO.corPrimaria)}">
${opcoes.previa ? '<meta name="robots" content="noindex">' : ''}
<style>${css(pagina.tema)}</style>
</head>
<body>
${corpo}
${js ? `<script>${js}</script>` : ''}
</body>
</html>`
}
